"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Wifi, WifiOff, Check, X } from "lucide-react";
import Link from "next/link";

export default function OfflineHero() {
  const { t } = useLang();
  const [online, setOnline] = useState(true);

  // Auto-toggle every 4 seconds for hero showcase effect
  useEffect(() => {
    const id = setInterval(() => setOnline((v) => !v), 4500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Works offline · Cash mode when WiFi drops", "Funciona offline · Modo efectivo si cae el WiFi")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("WiFi drops.", "El WiFi se cae.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Cash keeps ringing.", "El efectivo sigue.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "When WiFi drops, the POS switches to cash mode. Cash sales keep ringing and are saved on the device. When the signal comes back, they sync on their own — and any sale that can't sync is flagged for review. Cards, tabs and online orders wait for the connection.",
            "Si cae el WiFi, el POS pasa a modo efectivo. Las ventas en efectivo siguen y se guardan en el dispositivo. Cuando vuelve la señal, se sincronizan solas — y cualquier venta que no pueda sincronizar queda marcada para revisión. Tarjeta, tabs y pedidos online esperan la conexión."
          )}
        </motion.p>

        {/* Hero: WiFi toggle showcase */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[920px] mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.06)] mb-12"
        >
          {/* WiFi toggle UI */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                {t("WiFi status (auto-cycles)", "Estado WiFi (auto-cycles)")}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={online ? "on" : "off"}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-2"
                >
                  {online ? (
                    <Wifi className="w-5 h-5" style={{ color: "#2ECC71" }} />
                  ) : (
                    <WifiOff className="w-5 h-5" style={{ color: "#DC2626" }} />
                  )}
                  <span
                    className="text-[18px] font-black"
                    style={{ color: online ? "#2ECC71" : "#DC2626" }}
                  >
                    {online ? t("Connected", "Conectado") : t("Offline", "Offline")}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Manual toggle button */}
            <button
              onClick={() => setOnline((v) => !v)}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                online ? "bg-[#2ECC71]" : "bg-[#DC2626]"
              }`}
              aria-label="Toggle WiFi"
            >
              <motion.div
                animate={{ x: online ? 24 : 2 }}
                transition={{ duration: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-md"
              />
            </button>
          </div>

          {/* POS status card */}
          <div
            className="rounded-[20px] p-6 transition-colors duration-500"
            style={{
              backgroundColor: online ? "rgba(46,204,113,0.06)" : "rgba(244,114,43,0.06)",
              border: `1px solid ${online ? "rgba(46,204,113,0.25)" : "rgba(244,114,43,0.30)"}`,
            }}
          >
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                  {t("Redy POS", "Redy POS")}
                </div>
                <div className="text-[20px] font-black text-redy-charcoal">
                  {t("Status", "Estado")}
                </div>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={online ? "stat-on" : "stat-off"}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  className="text-right"
                >
                  <div
                    className="text-[32px] font-black leading-none tabular-nums"
                    style={{
                      background: online
                        ? "linear-gradient(135deg, #2ECC71 0%, #1FA055 100%)"
                        : "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {online ? t("Running", "Corriendo") : t("Cash mode", "Modo efectivo")}
                  </div>
                  <div className="text-[11px] font-bold text-redy-secondary mt-0.5">
                    {online
                      ? t("Real-time sync", "Sync en vivo")
                      : t("Offline mode — cash only", "Modo offline — solo efectivo")}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Capability check rows */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
              {[
                { label: t("Cash sales", "Ventas en efectivo"), worksOffline: true },
                { label: t("Card payments", "Pagos con tarjeta"), worksOffline: false },
                { label: t("Tabs", "Tabs"), worksOffline: false },
                { label: t("New KDS tickets", "Tickets nuevos al KDS"), worksOffline: false },
              ].map(({ label, worksOffline }) => {
                const ok = online || worksOffline;
                return (
                  <div key={label} className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: ok ? "#2ECC71" : "rgba(0,0,0,0.15)" }}
                    >
                      {ok ? (
                        <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                      ) : (
                        <X className="w-3 h-3 text-white" strokeWidth={3.5} />
                      )}
                    </div>
                    <span className="text-[11px] font-bold text-redy-charcoal">{label}</span>
                  </div>
                );
              })}
            </div>

            {/* Queue counter when offline */}
            <AnimatePresence>
              {!online && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-5 pt-5 border-t border-black/5"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[12px] font-bold text-redy-charcoal">
                      {t("Saved on the device, waiting to sync", "Guardadas en el dispositivo, esperando sync")}
                    </div>
                    <div className="text-[16px] font-black tabular-nums" style={{ color: "#F4722B" }}>
                      14 {t("cash sales", "ventas en efectivo")}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Keep selling when WiFi dies →", "Sigue vendiendo aunque muera el WiFi →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
